import React, { useContext } from "react";
import classNames from "classnames";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AccountingReportContext } from "../AccountingReport.context";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import { getDrillDownClassParams } from "../useGLDrillDownUrl";
import { buildGlDrillDownParams, GlDrillDownParams } from "../glDrillDown.lib";
import styles from "./ExpenseByVendorTable.module.scss";

interface Props {
    value: number;
    period: ReportPeriodWithLabel;
    vendorId: string;
    onDrillDown?: (params: GlDrillDownParams) => void;
}

/**
 * ViewTable-compatible Expense by Vendor value cell.
 * Renders as a <div> (not <td>) for CSS-grid row compatibility.
 *
 * Drill-down filters by counterparty only (no accountCodes), unlike P&L /
 * Balance Sheet.  The control stays active for legitimate zero values so users
 * can drill into vendors that happen to net to zero in a period.
 */
export const ExpenseByVendorViewValueCell: React.FC<Props> = React.memo(
    ({ value, period, vendorId, onDrillDown }) => {
        const { filters } = useContext(AccountingReportContext);

        const drillDownParams = buildGlDrillDownParams({
            startDate: period.startDate,
            endDate: period.endDate,
            entityId: period.entityId ?? filters.entityId,
            ledgerId: filters.ledgerId,
            counterpartiesIds: [vendorId],
            accountIds: filters.accountIds,
            subaccountIds: filters.subaccountIds,
            ...getDrillDownClassParams(period, filters),
        });

        const { text: displayValue, negativeClassName } =
            useReportAmountDisplay(value, {
                comparisonDiff: period.comparisonDiff,
            });

        return (
            <ViewTableCell
                className={classNames(styles.numericCell, negativeClassName)}
            >
                {onDrillDown ? (
                    <button
                        type="button"
                        className={classNames(
                            "data-table__drill-down",
                            styles.drillDownButton,
                        )}
                        onClick={() => onDrillDown(drillDownParams)}
                    >
                        {displayValue}
                    </button>
                ) : (
                    displayValue
                )}
            </ViewTableCell>
        );
    },
);
ExpenseByVendorViewValueCell.displayName = "ExpenseByVendorViewValueCell";
